/**
 * AssessmentAiPanel — VER-58 AI conversation surface (5-prompt cap).
 *
 * This component is the entry point for route-wiring.
 * Suggested route (add in Routes.tsx when ready): /assess/:assessmentId/ai
 *
 * IMPORTANT — mock-only surface:
 * All AI responses are generated locally by getMockAiResponse() in
 * assessmentAi.mock.ts. No network calls are made from this component.
 * Replace the mock call with a real API call when VER-55 / VER-58 backend
 * endpoints are ready.
 *
 * IMPORTANT — client cap is UX only:
 * The 5-prompt limit enforced here prevents the user from submitting more
 * than 5 prompts in the UI. The REAL security boundary is the server, which
 * must reject a 6th prompt with HTTP 429 (VER-55 / VER-58 backend work).
 * Never rely on this component's counter as the source of truth.
 *
 * TODO [RBAC]: Add permit("<AssessAiView>" code) guard at the route level
 *              once defined. Run verbal-rbac-review before merge.
 * TODO [CONSENT]: This surface displays AI analysis over participant
 *                 assessment data. Run verbal-consent-audit before releasing.
 * TODO [DATA]: Replace assessmentTitle prop with a real assessment fetch
 *              (participant name, programme, WEMWBS scores) for context injection.
 * TODO [WAVE-2]: Pass assessment context (pre/post WEMWBS scores, narrative
 *                summary) to the real AI endpoint so responses are grounded.
 */

import React, { useRef, useEffect, useState } from "react";
import { Alert, Box, Card, CardContent, CircularProgress, Divider, Typography } from "@mui/material";
import { SmartToyOutlined } from "@mui/icons-material";
import { AiPromptCounter } from "./AiPromptCounter";
import { AiMessageBubble } from "./AiMessageBubble";
import { AiComposer } from "./AiComposer";
import { AiLimitReachedBanner } from "./AiLimitReachedBanner";
import { AiMessage, AiSessionState, AI_PROMPT_LIMIT, createEmptySession, getMockAiResponse } from "./assessmentAi.mock";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type AssessmentAiPanelProps = {
    /** Display name for the assessment — shown in the panel header. */
    assessmentTitle: string;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AssessmentAiPanel = ({ assessmentTitle }: AssessmentAiPanelProps): JSX.Element => {
    const [session, setSession] = useState<AiSessionState>(createEmptySession);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const messageListRef = useRef<HTMLDivElement>(null);

    // Scroll to the bottom of the chat whenever messages change or loading state changes.
    useEffect(() => {
        if (messageListRef.current) {
            messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }
    }, [session.messages, isLoading]);

    // -----------------------------------------------------------------------
    // Send handler
    // -----------------------------------------------------------------------

    const handleSend = async (text: string) => {
        if (session.limitReached || isLoading) {
            return;
        }

        setError(null);

        const practitionerMsg: AiMessage = {
            id: `practitioner-${Date.now()}`,
            role: "practitioner",
            text,
            timestamp: new Date().toISOString(),
        };

        const nextPromptsUsed = session.promptsUsed + 1;

        // Append practitioner message immediately, mark loading.
        setSession((prev) => ({
            ...prev,
            messages: [...prev.messages, practitionerMsg],
            promptsUsed: nextPromptsUsed,
            limitReached: nextPromptsUsed >= AI_PROMPT_LIMIT,
        }));

        setIsLoading(true);

        try {
            // MOCK: replace with real API call when VER-55 / VER-58 backend is ready.
            // Real call shape (example):
            //   POST /api/assess/sessions/:sessionId/ai-prompt
            //   Body: { prompt: text, assessmentId }
            //   Response: { reply: string }
            //   HTTP 429 when server-side quota exhausted.
            const reply = await getMockAiResponse(nextPromptsUsed - 1);

            const aiMsg: AiMessage = {
                id: `ai-${Date.now()}`,
                role: "ai",
                text: reply,
                timestamp: new Date().toISOString(),
            };

            setSession((prev) => ({
                ...prev,
                messages: [...prev.messages, aiMsg],
            }));
        } catch {
            // Roll back the optimistic quota increment and drop the unanswered prompt so a
            // failed send doesn't consume one of the practitioner's prompts.
            setSession((prev) => {
                const rolledBack = Math.max(0, prev.promptsUsed - 1);
                return {
                    ...prev,
                    messages: prev.messages.filter((m) => m.id !== practitionerMsg.id),
                    promptsUsed: rolledBack,
                    limitReached: rolledBack >= AI_PROMPT_LIMIT,
                };
            });
            setError("The AI assistant could not respond. Please try again or contact support.");
        } finally {
            setIsLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------------

    const isEmpty = session.messages.length === 0 && !isLoading;

    return (
        <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <CardContent sx={{ pb: 0, flexShrink: 0 }}>
                {/* Panel header */}
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <SmartToyOutlined color="primary" />
                        <Typography variant="h6" component="h2">
                            AI Assessment Assistant
                        </Typography>
                    </Box>
                    <AiPromptCounter promptsUsed={session.promptsUsed} />
                </Box>

                <Typography variant="body2" color="textSecondary" mb={1.5}>
                    {assessmentTitle}
                </Typography>

                <Divider />
            </CardContent>

            {/* Message list */}
            <Box ref={messageListRef} flex={1} overflow="auto" px={2} pt={2} pb={1} sx={{ minHeight: 320 }}>
                {isEmpty ? (
                    // Empty state — no messages yet
                    <Box
                        height="100%"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        py={4}
                        textAlign="center"
                    >
                        <SmartToyOutlined sx={{ fontSize: "3rem", color: "primary.light" }} />
                        <Typography variant="subtitle1" color="textPrimary">
                            Ask about this assessment
                        </Typography>
                        <Typography variant="body2" color="textSecondary" maxWidth={400}>
                            You have {AI_PROMPT_LIMIT} prompts available for this session. The AI can help you interpret
                            WEMWBS scores, identify patterns, and suggest next steps.
                        </Typography>
                        <Typography variant="caption" color="textSecondary" maxWidth={400} mt={0.5}>
                            AI responses are analytical suggestions only and must not be used as clinical diagnoses.
                        </Typography>
                    </Box>
                ) : (
                    session.messages.map((msg) => <AiMessageBubble key={msg.id} message={msg} />)
                )}

                {/* Typing indicator while mock responds */}
                {isLoading ? (
                    <Box display="flex" alignItems="center" gap={1} mb={2} pl={1}>
                        <CircularProgress size={16} color="primary" />
                        <Typography variant="caption" color="textSecondary">
                            AI is thinking…
                        </Typography>
                    </Box>
                ) : null}
            </Box>

            {/* Error alert */}
            {error !== null ? (
                <Box px={2} pb={1}>
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Box>
            ) : null}

            {/* Limit-reached banner */}
            {session.limitReached ? (
                <Box px={2} pb={1}>
                    <AiLimitReachedBanner />
                </Box>
            ) : null}

            {/* Composer footer */}
            <CardContent sx={{ pt: 1, flexShrink: 0 }}>
                <Divider sx={{ mb: 1.5 }} />
                <AiComposer
                    limitReached={session.limitReached}
                    isLoading={isLoading}
                    promptsUsed={session.promptsUsed}
                    onSend={handleSend}
                />
            </CardContent>
        </Card>
    );
};
